import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, filter } from 'rxjs';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';
import { SupportActivityTrackerService } from '../services/support-activity-tracker.service';
import { SupportApiError, SupportApiService } from '../services/support-api.service';
import { SupportChatService, newClientId } from '../services/support-chat.service';
import {
  SupportAttachmentNotice,
  SupportDraftAttachment,
  SupportUploadQueue,
  fileIcon,
  fileSize,
  hasFiles,
  namePastedImage,
  rejectionNotice,
  uploadFailureKey,
} from '../services/support-upload-queue';
import { SUPPORT_ATTACHMENT_LIMITS, SUPPORT_FEATURES } from '../support-system.config';
import {
  SupportErrorCode,
  SupportRequest,
  SupportRequestChange,
  SupportRequestContext,
  SupportRequestEvent,
  SupportRequestFields,
  SupportRequestFilter,
  SupportRequestInput,
  SupportRequestNudge,
  SupportRequestPage,
  SupportRequestType,
} from '../support-system.models';
import { RequestNudgePickerComponent } from './request-nudge-picker.component';
import { productForUrl, requestProducts } from './request-products';
import {
  REQUEST_ACCESS_PRODUCTS,
  REQUEST_CHANGES,
  REQUEST_EVENTS,
  REQUEST_GROUPS,
  REQUEST_PAGES,
  REQUEST_TYPES,
  RequestTypeDef,
} from './request-types';
import { SupportRequestsService } from './support-requests.service';

export interface SupportRequestsDialogData {
  /** Omitted when the account has a single shop. */
  target?: string;
  startTab?: Tab;
}

type Tab = 'new' | 'mine';
type Step = 'type' | 'form' | 'done';

const MAX_MESSAGE_LENGTH = 4000;
/** A scheduled switch runs within about a minute of its time: look after that. */
const SWITCH_CHECK_DELAY_MS = 70_000;
/** Further out, the 30-second check while the modal is open is enough. */
const SWITCH_CHECK_WITHIN_MS = 30 * 60_000;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LINK = /^https?:\/\/\S+\.\S+/i;

const ERROR_KEYS: Partial<Record<SupportErrorCode, string>> = {
  OFFLINE: 'support_requests.error_network',
  TIMEOUT: 'support_requests.error_network',
  RATE_LIMITED: 'support_requests.error_rate_limited',
  INVALID_REQUEST: 'support_requests.error_invalid',
  FORBIDDEN: 'support_requests.error_forbidden',
};

/** Everything the form asks, in one place, so a type change starts clean. */
function emptyForm() {
  return {
    nudgeIds: [] as string[],
    pages: [] as SupportRequestPage[],
    action: 'pause' as 'pause' | 'on',
    changes: [] as SupportRequestChange[],
    url: '',
    when: 'now' as 'now' | 'date',
    whenDay: null as Date | null,
    whenTime: '00:00',
    until: 'manual' as 'manual' | 'date',
    untilDay: null as Date | null,
    untilTime: '23:45',
    product: null as string | null,
    periodFrom: null as Date | null,
    periodTo: null as Date | null,
    attachView: true,
    name: '',
    email: '',
    accessProducts: [] as string[],
    events: [] as SupportRequestEvent[],
    from: null as Date | null,
    to: null as Date | null,
    message: '',
  };
}
type RequestForm = ReturnType<typeof emptyForm>;

/** The day at that time ("HH:mm"), in the browser's time zone. */
function dayAt(day: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number);
  const at = new Date(day);
  at.setHours(hours, minutes, 0, 0);
  return at;
}

/** YYYY-MM-DD of a picked day (local, not UTC). */
function dayKey(day: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Requests: one modal for everything a client asks the team for. New
 * request picks a type, fills that type's form and sends it; My requests
 * lists what was sent and where it stands. The shop is never asked: it is
 * the one selected in the dashboard.
 */
@Component({
  selector: 'app-support-requests-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextFieldModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    TranslateModule,
    RequestNudgePickerComponent,
  ],
  templateUrl: './support-requests-dialog.component.html',
  styleUrls: ['./support-requests-dialog.component.css'],
})
export class SupportRequestsDialogComponent implements OnInit, OnDestroy {
  readonly data = inject<SupportRequestsDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<SupportRequestsDialogComponent>);
  private dialog = inject(MatDialog);
  private api = inject(SupportApiService);
  private chat = inject(SupportChatService);
  private tracker = inject(SupportActivityTrackerService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  readonly requests = inject(SupportRequestsService);

  @ViewChild('filePicker') filePicker?: ElementRef<HTMLInputElement>;

  readonly types = REQUEST_TYPES;
  readonly groups = REQUEST_GROUPS;
  readonly pageOptions = REQUEST_PAGES;
  readonly changeOptions = REQUEST_CHANGES;
  readonly eventOptions = REQUEST_EVENTS;
  readonly maxLength = MAX_MESSAGE_LENGTH;
  readonly maxFileMb = SUPPORT_ATTACHMENT_LIMITS.maxBytes / (1024 * 1024);
  readonly fileAccept = SUPPORT_ATTACHMENT_LIMITS.mimeTypes.join(',');
  readonly fileIcon = fileIcon;
  readonly fileSize = fileSize;
  /**
   * Days come from Material's datepicker and times from 15-minute presets,
   * like the Signals experiment schedule (native date-time inputs misbehave).
   */
  readonly timeOptions = Array.from(
    { length: 96 },
    (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`,
  );
  readonly today = startOfToday();

  /** The products the client can open, for "Question about results". */
  readonly products = requestProducts(
    this.localStorageService.get<string[]>('privileges') || [],
  );
  /** A colleague can only be given what the client has. */
  readonly accessOptions = REQUEST_ACCESS_PRODUCTS.filter((p) =>
    this.products.some((o) => o.key === p),
  );
  /** Where the client was when opening the modal (preselects the product). */
  private readonly openedOn = this.router.url;
  /** The dashboard's date range when the modal was opened. */
  private readonly dateRange = this.readDateRange();

  readonly tab = signal<Tab>(this.data.startTab || 'new');
  readonly step = signal<Step>('type');
  readonly def = signal<RequestTypeDef | null>(null);
  form: RequestForm = emptyForm();
  /** The form as it was when the type was picked: unchanged means nothing to lose. */
  private pristine = '';

  readonly files = new SupportUploadQueue((file) => this.chat.uploadRequestAttachment(file));
  readonly attachmentNotice = signal<SupportAttachmentNotice | null>(null);
  readonly dragging = signal(false);
  private dragDepth = 0;

  /* The target's nudges, loaded the first time a form asks for them. */
  readonly nudgesState = signal<'loading' | 'ready' | 'error'>('loading');
  readonly nudges = signal<SupportRequestNudge[]>([]);
  private nudgesRequest?: Subscription;
  private nudgesLoaded = false;

  readonly sending = signal(false);
  readonly errorKey = signal<string | null>(null);
  readonly sent = signal<{ id: string; email: boolean } | null>(null);
  /** The last attempt: a retry of the same request reuses its id. */
  private attempt: { id: string; content: string } | null = null;

  /* My requests */
  readonly filter = signal<SupportRequestFilter>('open');
  readonly mineState = signal<'loading' | 'ready' | 'error'>('loading');
  readonly mine = signal<SupportRequest[]>([]);
  private mineRequest?: Subscription;
  /** One check shortly after each scheduled switch of the requests on screen. */
  private switchChecks: ReturnType<typeof setTimeout>[] = [];

  private closeEvents = new Subscription();

  ngOnInit(): void {
    // Closing asks first when something was typed (see requestClose).
    this.dialogRef.disableClose = true;
    this.closeEvents.add(this.dialogRef.backdropClick().subscribe(() => this.requestClose()));
    this.closeEvents.add(
      this.dialogRef
        .keydownEvents()
        .pipe(filter((event) => event.key === 'Escape' && !event.defaultPrevented))
        .subscribe(() => this.requestClose()),
    );
    if (this.tab() === 'mine') this.loadMine();
    // A request can be finished (or a scheduled pause run) while it's on
    // screen: re-check every 30 s and reload the list when something moved.
    this.requests.watchModal();
    this.closeEvents.add(
      this.requests.changed$.subscribe(() => {
        if (this.tab() === 'mine') this.loadMine(true);
      }),
    );
  }

  ngOnDestroy(): void {
    this.requests.unwatchModal();
    this.clearSwitchChecks();
    this.closeEvents.unsubscribe();
    this.nudgesRequest?.unsubscribe();
    this.mineRequest?.unsubscribe();
    this.files.clear();
    // Something may have changed (a new request, a status seen).
    this.requests.refresh();
  }

  /* -------------------------------- Tabs --------------------------------- */

  goNew(): void {
    this.tab.set('new');
    if (this.step() === 'done') this.startOver();
  }

  goMine(): void {
    this.tab.set('mine');
    this.loadMine();
  }

  /* ------------------------------ Step 1 -------------------------------- */

  typesIn(group: string): RequestTypeDef[] {
    return this.types.filter((t) => t.group === group);
  }

  /** A different type starts a fresh form; the same one keeps what was typed. */
  pick(def: RequestTypeDef): void {
    if (this.def()?.type !== def.type) {
      this.form = this.initialForm(def.type);
      this.pristine = JSON.stringify(this.form);
      this.files.clear();
      this.attachmentNotice.set(null);
      this.attempt = null;
    }
    this.def.set(def);
    this.errorKey.set(null);
    this.step.set('form');
    if (def.nudges && !this.nudgesLoaded) this.loadNudges();
  }

  backToTypes(): void {
    if (this.sending()) return;
    this.step.set('type');
  }

  private initialForm(type: SupportRequestType): RequestForm {
    const form = emptyForm();
    if (type === 'data') {
      form.product = productForUrl(this.products, this.openedOn);
      form.periodFrom = this.dateRange?.fromDay || null;
      form.periodTo = this.dateRange?.toDay || null;
    }
    if (type === 'access' && this.accessOptions.includes('Nudge')) {
      form.accessProducts = ['Nudge'];
    }
    return form;
  }

  /* ------------------------------ Step 2 -------------------------------- */

  loadNudges(): void {
    this.nudgesState.set('loading');
    this.nudgesRequest?.unsubscribe();
    this.nudgesRequest = this.api.requestNudges(this.data.target).subscribe({
      next: ({ nudges }) => {
        this.nudges.set(nudges || []);
        this.nudgesLoaded = true;
        this.nudgesState.set('ready');
      },
      error: () => this.nudgesState.set('error'),
    });
  }

  typeKey(suffix: string, type = this.def()?.type): string {
    return `support_requests.types.${type}.${suffix}`;
  }

  isIn<T>(list: T[], value: T): boolean {
    return list.includes(value);
  }

  /** Chips that can be picked together. */
  toggleIn<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  get emailInvalid(): boolean {
    const email = this.form.email.trim();
    return !!email && !EMAIL.test(email);
  }

  get urlInvalid(): boolean {
    const url = this.form.url.trim();
    return !!url && !LINK.test(url);
  }

  /** Toggle: the start and end, when picked as dates. */
  get scheduleError(): string | null {
    const { when, whenDay, whenTime, until, untilDay, untilTime } = this.form;
    const picked = when === 'date' && whenDay ? dayAt(whenDay, whenTime).getTime() : null;
    if (picked !== null && picked < Date.now() - 60_000) return 'support_requests.date_past';
    const start = picked ?? Date.now();
    if (until === 'date' && untilDay && dayAt(untilDay, untilTime).getTime() <= start) {
      return 'support_requests.until_before_start';
    }
    return null;
  }

  /** Data and heads-up: a range whose end is before its start. */
  rangeInvalid(from: Date | null, to: Date | null): boolean {
    return !!from && !!to && dayKey(to) < dayKey(from);
  }

  get canSend(): boolean {
    const def = this.def();
    const f = this.form;
    if (!def || this.sending() || !this.files.settled()) return false;
    if (def.messageRequired && !f.message.trim()) return false;
    switch (def.type) {
      case 'toggle':
        return (
          (f.when === 'now' || !!f.whenDay) &&
          (f.until === 'manual' || !!f.untilDay) &&
          !this.scheduleError
        );
      case 'broken':
        return !this.urlInvalid;
      case 'data':
        return (
          !!f.product && !!f.periodFrom && !!f.periodTo && !this.rangeInvalid(f.periodFrom, f.periodTo)
        );
      case 'access':
        return !!f.name.trim() && !!f.email.trim() && !this.emailInvalid;
      case 'headsup':
        return (
          (!!f.events.length || !!f.message.trim()) && !this.rangeInvalid(f.from, f.to)
        );
      default:
        return true;
    }
  }

  /* -------------------------------- Send -------------------------------- */

  send(): void {
    const def = this.def();
    if (!def || !this.canSend) return;
    const f = this.form;
    const message = f.message.trim();
    const nudgeIds = def.nudges ? f.nudgeIds : [];
    // Optional fields only when set: the validation is strict.
    const request: Omit<SupportRequestInput, 'clientRequestId' | 'context' | 'diagnostics'> = {
      ...(this.data.target ? { target: this.data.target } : {}),
      type: def.type,
      fields: this.fieldsFor(def.type),
      ...(message ? { message } : {}),
      ...(nudgeIds.length ? { nudgeIds } : {}),
      ...(def.attachments && this.files.count()
        ? { attachmentIds: this.files.items().map((i) => i.attachment!.id) }
        : {}),
    };
    const content = JSON.stringify(request);
    if (this.attempt?.content !== content) this.attempt = { id: newClientId(), content };
    const snapshot = this.tracker.snapshot();
    const withView = def.diagnostics || (def.type === 'data' && f.attachView);

    this.sending.set(true);
    this.errorKey.set(null);
    this.api
      .createRequest({
        ...request,
        context: this.context(snapshot.context),
        ...(withView ? { diagnostics: snapshot } : {}),
        clientRequestId: this.attempt.id,
      })
      .subscribe({
        next: ({ request: created, email }) => {
          this.sending.set(false);
          this.attempt = null;
          this.sent.set({ id: created.id, email: email?.status === 'queued' });
          this.step.set('done');
          this.files.clear();
          this.requests.refresh();
        },
        error: (error: SupportApiError) => {
          this.sending.set(false);
          this.errorKey.set(ERROR_KEYS[error.code] || 'support_requests.error_generic');
          // Not a retry of the same thing any more: the next try gets a new id.
          if (error.code === 'CONFLICT') this.attempt = null;
          // A chosen nudge may be gone meanwhile.
          if (error.code === 'INVALID_REQUEST' && nudgeIds.length) this.loadNudges();
        },
      });
  }

  /** The type's own fields, with unset optional ones left out. */
  private fieldsFor(type: SupportRequestType): SupportRequestFields[SupportRequestType] {
    const f = this.form;
    switch (type) {
      case 'toggle':
        return {
          action: f.action,
          start: f.when === 'date' && f.whenDay ? dayAt(f.whenDay, f.whenTime).toISOString() : 'now',
          until:
            f.until === 'date' && f.untilDay
              ? dayAt(f.untilDay, f.untilTime).toISOString()
              : 'manual',
        };
      case 'change':
        return { changeKinds: f.changes };
      case 'broken':
        return f.url.trim() ? { url: f.url.trim() } : {};
      case 'data':
        return {
          product: f.product!,
          periodFrom: dayKey(f.periodFrom!),
          periodTo: dayKey(f.periodTo!),
          attachView: f.attachView,
        };
      case 'idea':
        return { pages: f.pages };
      case 'access':
        return { name: f.name.trim(), email: f.email.trim(), products: f.accessProducts };
      case 'headsup':
        return {
          eventTypes: f.events,
          ...(f.from ? { from: dayKey(f.from) } : {}),
          ...(f.to ? { to: dayKey(f.to) } : {}),
        };
      default:
        return {};
    }
  }

  private context(base: SupportRequestContext | Omit<SupportRequestContext, 'dateRange'>): SupportRequestContext {
    const range = this.dateRange;
    return { ...base, dateRange: range ? { from: range.from, to: range.to } : null };
  }

  /** The date picker's range, saved as `<iso>@<timezone>`. */
  private readDateRange(): { from: string; to: string; fromDay: Date | null; toDay: Date | null } | null {
    const start = this.localStorageService.get<string>('startDate');
    const end = this.localStorageService.get<string>('endDate');
    if (!start || !end) return null;
    const [from, timezone] = String(start).split('@');
    const [to] = String(end).split('@');
    /** The calendar day of that instant in the picker's time zone. */
    const day = (value: string): Date | null => {
      const date = new Date(value);
      if (isNaN(date.getTime())) return null;
      let key = value.slice(0, 10);
      try {
        // en-CA writes YYYY-MM-DD.
        key = new Intl.DateTimeFormat('en-CA', { timeZone: timezone || undefined }).format(date);
      } catch {}
      const [y, m, d] = key.split('-').map(Number);
      return new Date(y, m - 1, d);
    };
    return { from, to, fromDay: day(from), toDay: day(to) };
  }

  /* ------------------------------ Step 3 -------------------------------- */

  /** "Send another": back to the types with nothing left over. */
  startOver(): void {
    this.def.set(null);
    this.form = emptyForm();
    this.pristine = JSON.stringify(this.form);
    this.sent.set(null);
    this.errorKey.set(null);
    this.files.clear();
    this.step.set('type');
    this.tab.set('new');
  }

  viewMine(): void {
    this.startOver();
    this.goMine();
  }

  /** The reference in bold, inside a translated sentence. */
  sentText(id: string): string {
    const safe = id.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    return this.translate.instant(
      this.sent()?.email ? 'support_requests.sent_text' : 'support_requests.sent_text_no_email',
      { id: `<strong>${safe}</strong>` },
    );
  }

  /* ---------------------------- My requests ----------------------------- */

  setFilter(value: SupportRequestFilter): void {
    if (this.filter() === value) return;
    this.filter.set(value);
    this.loadMine();
  }

  /** `quiet`: a background reload keeps the list on screen while it loads, and on errors. */
  loadMine(quiet = false): void {
    const keep = quiet && this.mineState() === 'ready';
    if (!keep) this.mineState.set('loading');
    this.mineRequest?.unsubscribe();
    this.mineRequest = this.api.requests(this.filter(), this.data.target).subscribe({
      next: (page) => {
        this.mine.set(page.items || []);
        this.mineState.set('ready');
        this.scheduleSwitchChecks();
      },
      error: () => {
        if (!keep) this.mineState.set('error');
      },
    });
  }

  /**
   * "Pause or turn on" requests with a switch coming up soon: look again
   * just after it ran, so the row updates without waiting for the next
   * 30-second check.
   */
  private scheduleSwitchChecks(): void {
    this.clearSwitchChecks();
    const now = Date.now();
    const times = this.mine()
      .flatMap((request) => request.switch?.scheduled || [])
      .map((step) => new Date(step.at).getTime() + SWITCH_CHECK_DELAY_MS - now)
      .filter((wait) => wait > 0 && wait <= SWITCH_CHECK_WITHIN_MS);
    for (const wait of new Set(times)) {
      this.switchChecks.push(
        setTimeout(() => {
          this.requests.refresh();
          if (this.tab() === 'mine') this.loadMine(true);
        }, wait),
      );
    }
  }

  private clearSwitchChecks(): void {
    this.switchChecks.forEach(clearTimeout);
    this.switchChecks = [];
  }

  /** "Something looks broken · Product pages · Nov 4" */
  meta(request: SupportRequest): string {
    const parts = [this.translate.instant(this.typeKey('title', request.type))];
    const nudges = request.nudgeIds?.length || 0;
    if (nudges) {
      parts.push(
        this.translate.instant(
          nudges === 1 ? 'support_requests.one_nudge' : 'support_requests.n_nudges',
          { count: nudges },
        ),
      );
    } else if (request.pages?.length) {
      const first = this.translate.instant(`support_requests.pages.${request.pages[0]}`);
      parts.push(request.pages.length > 1 ? `${first} +${request.pages.length - 1}` : first);
    }
    const date = new Date(request.createdAt);
    if (!isNaN(date.getTime())) {
      parts.push(
        date.toLocaleDateString(this.translate.currentLang || 'en', {
          month: 'short',
          day: 'numeric',
        }),
      );
    }
    return parts.join(' · ');
  }

  trackRequest(_: number, request: SupportRequest): string {
    return request.id;
  }

  /* -------------------------------- Close -------------------------------- */

  private get dirty(): boolean {
    return (
      this.tab() === 'new' &&
      this.step() === 'form' &&
      (JSON.stringify(this.form) !== this.pristine || !!this.files.count())
    );
  }

  /** Unsent text asks first: "Discard this request?" */
  requestClose(): void {
    if (this.sending()) return;
    if (!this.dirty) {
      this.dialogRef.close();
      return;
    }
    // Leave the field first: the prompt taking focus mid-render would flip
    // its touched state during change detection.
    (document.activeElement as HTMLElement | null)?.blur();
    this.dialog
      .open(ConfirmationComponent, {
        width: '430px',
        data: {
          header: this.translate.instant('support_requests.discard_title'),
          content: this.translate.instant('support_requests.discard_text'),
          confirm_btn: this.translate.instant('support_requests.discard_confirm'),
          cancel_btn: this.translate.instant('support_requests.keep_editing'),
          confirmButtonDangerStyle: true,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 1) this.dialogRef.close();
      });
  }

  /* -------------------------------- Files -------------------------------- */

  get canAttach(): boolean {
    return (
      !!this.def()?.attachments &&
      SUPPORT_FEATURES.attachments &&
      this.chat.attachmentsAvailable()
    );
  }

  pickFiles(): void {
    this.filePicker?.nativeElement.click();
  }

  onFilesPicked(input: HTMLInputElement): void {
    this.addFiles(Array.from(input.files || []));
    input.value = '';
  }

  /** Screenshots pasted into the message become attachments. */
  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files || []);
    if (!files.length || !this.canAttach) return;
    event.preventDefault();
    this.addFiles(files.map(namePastedImage));
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canAttach || this.step() !== 'form' || this.tab() !== 'new' || !hasFiles(event)) {
      return;
    }
    event.preventDefault();
    this.dragDepth++;
    this.dragging.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.dragging()) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onDragLeave(): void {
    if (!this.dragging()) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (!this.dragDepth) this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.dragging()) return;
    event.preventDefault();
    this.dragDepth = 0;
    this.dragging.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files || []));
  }

  removeFile(localId: string): void {
    this.files.remove(localId);
    this.attachmentNotice.set(null);
  }

  uploadFailureKey(item: SupportDraftAttachment): string {
    return uploadFailureKey(item);
  }

  private addFiles(files: File[]): void {
    if (!files.length) return;
    this.attachmentNotice.set(rejectionNotice(this.files.add(files)));
  }
}
